import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Globe, ExternalLink, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds/app";

export default function LandingPage() {
  const { cardId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, biz: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cards/${cardId}`);
        // record scan (fire and forget)
        api.post(`/cards/${cardId}/scan`).catch(() => {});
        setState({ loading: false, error: null, biz: data });
      } catch (err) {
        const status = err.response?.status;
        setState({
          loading: false,
          error: status === 410 ? "deactivated" : "notfound",
          biz: null,
        });
      }
    })();
  }, [cardId]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-1)]">
        <div className="text-[var(--fg-3)] text-xs uppercase tracking-[0.25em] font-sans animate-pulse">
          Caricamento...
        </div>
      </div>
    );
  }

  if (state.error === "deactivated" || state.error === "notfound" || !state.biz) {
    return (
      <div
        data-testid={LANDING.publicDeactivated}
        className="min-h-screen flex items-center justify-center bg-[var(--bg-1)] p-6"
      >
        <div className="max-w-md text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-3)] mb-6">
            <XCircle size={26} strokeWidth={1.4} />
          </div>
          <h1 className="font-serif text-3xl text-[var(--fg-1)] tracking-tight">
            Questa pagina non è <span className="italic">al momento disponibile</span>
          </h1>
          <p className="mt-3 text-sm text-[var(--fg-3)] leading-relaxed">
            Riprova più tardi o contatta l'attività per maggiori informazioni.
          </p>
        </div>
      </div>
    );
  }

  const biz = state.biz;

  return (
    <div
      data-testid={LANDING.publicContainer}
      className="min-h-screen bg-[var(--bg-1)] flex items-center justify-center p-6"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F2F4] font-sans font-semibold">
            Powered by
          </div>
          <div className="mt-1 font-serif text-2xl tracking-tight leading-none text-[var(--fg-1)]">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </div>
        </div>

        <div className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-2xl p-8 shadow-[0_2px_20px_rgba(15,15,16,0.06)]">
          <div className="flex items-center justify-center gap-1 text-[#E9A94E] mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={16} className="fill-[#E9A94E]" strokeWidth={1.5} />
            ))}
          </div>
          <h1 className="font-serif text-4xl text-[var(--fg-1)] tracking-tight leading-tight text-center">
            {biz.name}
          </h1>
          {biz.address && (
            <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[var(--fg-3)] font-sans">
              <MapPin size={12} /> {biz.address}
            </div>
          )}

          <p className="mt-8 text-center text-sm text-[var(--fg-2)] leading-relaxed">
            La tua opinione conta. <span className="italic font-serif">Bastano 30 secondi</span> per lasciarci una recensione su Google.
          </p>

          {biz.google_business_url ? (
            <a
              href={biz.google_business_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={LANDING.publicReviewCta}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] rounded-full py-3.5 px-6 font-sans text-sm font-semibold focus:ring-2 focus:ring-[#00F2F4]/50 focus:outline-none"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <Star size={15} className="fill-[#0A0A0A]" strokeWidth={0} />
              Lascia una recensione
              <ArrowRight size={14} />
            </a>
          ) : (
            <div className="mt-6 text-center text-xs text-[var(--fg-4)] p-4 border border-dashed border-[var(--border-c)] rounded-lg">
              Link Google non ancora configurato. Chiedi all'attività di completarlo.
            </div>
          )}

          {biz.website_url && (
            <a
              href={biz.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] rounded-full py-3 px-6 font-sans text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Globe size={14} /> Visita il sito
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </motion.div>
      <div className="mt-8 text-center text-[10px] text-[var(--fg-4)] font-sans">
        <a href="/privacy" className="hover:text-[var(--fg-2)] underline">Privacy</a>
        <span className="mx-2">·</span>
        <a href="/terms" className="hover:text-[var(--fg-2)] underline">Termini</a>
      </div>
    </div>
  );
}
